import React, { useRef, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import ArchiveModelModalV2 from "../../../ArchiveModelModal/ArchiveModelModalV2";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Chip from "@src/sharedComponents/Chip/Chip";
import Popover from "@src/sharedComponents/Popover/Popover";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import styles from "../ModelDrawer.module.css";

type HeaderProps = {
  displayName: string;
  provider: string;
  isLoading: boolean;
  portfolioPlanId?: PortfolioPlanId;
  showMenuOptions?: boolean;
};

const DrawerHeader = ({
  displayName,
  provider,
  isLoading,
  portfolioPlanId,
  showMenuOptions,
}: HeaderProps) => {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const { enablePortfolioLabPhase2 } = useFlags();

  const auth = useAdvisorRequestAuth();
  const { data: portfolioPlan } = useGetPortfolioPlanById(
    portfolioPlanId === undefined ? null : portfolioPlanId,
    { $expand: ["accountsAppliedCount", "activeClientCount"] },
    auth
  );

  const onNavToEditPortfolio = () => {
    if (portfolioPlanId === undefined) {
      return;
    }

    navigate(
      `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/${portfolioPlanId}/Update`
    );
  };

  return (
    <div className={styles.headerFlexApart}>
      <div className={styles.flexRow}>
        {isLoading === true ? (
          <Skeleton width={200} />
        ) : (
          <>
            <div className={styles.title}>{displayName}</div>
            {/* popover should only be visible for admin users */}
            {portfolioPlanId === undefined ||
            showMenuOptions !== true ||
            portfolioPlan?.data?.plan.accountsAppliedCount ===
              undefined ? null : (
              <>
                <ArchiveModelModalV2
                  isOpen={isModalOpen}
                  setIsOpen={setIsModalOpen}
                  portfolioPlanId={portfolioPlanId}
                />

                <Popover
                  open={showPopover}
                  onClose={() => setShowPopover(false)}
                  anchor={anchorRef.current}
                  popoverElement={
                    <div
                      className={styles.dotMenu}
                      ref={anchorRef}
                      onClick={() => setShowPopover(true)}
                    >
                      <MoreHorizIcon />
                    </div>
                  }
                >
                  <div className={styles.popoverContent}>
                    {enablePortfolioLabPhase2 &&
                      portfolioPlanId !== undefined && (
                        <Text
                          className={styles.editText}
                          variant="body"
                          color="text"
                          onClick={onNavToEditPortfolio}
                        >
                          Edit
                        </Text>
                      )}

                    {portfolioPlan.data.plan.accountsAppliedCount !== 0 ? (
                      <Tooltip
                        tooltipText={
                          <Text variant="bodySmall">
                            {`This model is applied to ${pluralize(
                              portfolioPlan?.data?.plan?.accountsAppliedCount,
                              "account",
                              true
                            )} and cannot be archived`}
                          </Text>
                        }
                        placement={"top"}
                      >
                        <Text
                          style={{ opacity: 0.5 }}
                          variant="body"
                          className={styles.archiveText}
                        >
                          Archive
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text
                        variant="body"
                        className={styles.archiveText}
                        onClick={() => {
                          setIsModalOpen(true);
                          setShowPopover(false);
                        }}
                      >
                        Archive
                      </Text>
                    )}
                  </div>
                </Popover>
              </>
            )}
          </>
        )}
      </div>

      <div className={styles.pillsDiv}>
        {isLoading === true ? (
          <Skeleton width={80} />
        ) : (
          <Chip label={provider} />
        )}
      </div>
    </div>
  );
};

export default DrawerHeader;
